import React, { useMemo } from "react";
import { Button, Card, Col, Form, OverlayTrigger, Popover, Row } from "react-bootstrap";
import Select from "react-select";
import { ReactComponent as Info } from "../../../../images/Info.svg";
import "./SLMPropertyAttributesPanel.css";

const getLookupList = (slmLookups, lookupKey) => {
  if (!Array.isArray(slmLookups)) {
    return [];
  }

  const lookupGroup = slmLookups.find((item) => item?.look_up === lookupKey);
  return Array.isArray(lookupGroup?.list) ? lookupGroup.list : [];
};

const mapLookupList = (list, candidateValueKeys, candidateLabelKeys) => {
  const firstDefinedValue = (option, keys) => {
    for (const key of keys) {
      if (option && Object.prototype.hasOwnProperty.call(option, key) && option[key] !== undefined && option[key] !== null) {
        return option[key];
      }
    }

    return undefined;
  };

  return list
    .map((option) => {
      if (typeof option === "string") {
        return { value: option, label: option };
      }

      const valueFromCandidates = firstDefinedValue(option, candidateValueKeys);
      const labelFromCandidates = firstDefinedValue(option, candidateLabelKeys);

      const value = valueFromCandidates ?? "";
      const label = labelFromCandidates ?? String(value);

      return {
        ...option,
        value,
        label,
      };
    })
    .filter((option) => option.value !== undefined && option.value !== null);
};

export default function SLMPropertyAttributesPanel({
  state,
  slmLookups,
  areaBasisOptions,
  carSpaceOptions,
  qualityTypeOptions,
  zoningOptions,
  numberFormatter,
  currencyFormatter,
  goLiveDate,
  onChange,
  onBlur,
  onNumberChange,
  onAreaBasisChange,
  onCarSpaceChange,
  onQualityTypeChange,
  onZoningChange,
  createId,
}) {
  const derivedAreaBasisOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "building_area_basis_category"),
      ["value", "area_basis_cat_id", "building_area_basis_category_id", "id", "code", "name", "label"],
      ["label", "area_basis", "building_area_basis_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : areaBasisOptions || [];
  }, [slmLookups, areaBasisOptions]);

  const derivedCarSpaceOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "car_space_category"),
      ["value", "car_space_descr_cat_id", "car_space_category_id", "id", "code", "name", "label"],
      ["label", "car_space_describer", "car_space_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : carSpaceOptions || [];
  }, [slmLookups, carSpaceOptions]);

  const derivedQualityTypeOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "quality_type"),
      ["value", "quality_type_id", "id", "code", "name", "label"],
      ["label", "quality_type", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : qualityTypeOptions || [];
  }, [slmLookups, qualityTypeOptions]);

  const derivedZoningOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "sales_zoning_category"),
      ["value", "zoning_cat_id", "sales_zoning_category_id", "id", "code", "name", "label"],
      ["label", "zoning", "sales_zoning_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : zoningOptions || [];
  }, [slmLookups, zoningOptions]);

  return (
    <Card className="mb-2">
      <Card.Header>Property Attributes</Card.Header>

      <Card.Body>
        <Row>
          <Col>
            <Form.Group as={Row} controlId="complete_year">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Year Built
              </Form.Label>
              <Col>
                <Form.Control onChange={onNumberChange} value={state.complete_year} as="input" type="text" onBlur={onBlur} />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="refurb_year">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Year Last Refurb
              </Form.Label>
              <Col>
                <Form.Control onChange={onNumberChange} value={state.refurb_year} as="input" type="text" onBlur={onBlur} />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="floors">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Storeys
              </Form.Label>
              <Col>
                <Form.Control onChange={onChange} value={state.floors} as="input" type="text" onBlur={onBlur} />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="floor_area">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Bldg/Unit Area
              </Form.Label>
              <Col>
                <Form.Control onChange={onNumberChange} value={numberFormatter.format(state.floor_area)} as="input" type="text" onBlur={onBlur} />
              </Col>
              <Col>{state.sale_price && state.floor_area ? currencyFormatter.format((parseFloat(state.sale_price.replace(/[^0-9.]/g, "")) || 0) / (state.floor_area || 1)) + " per sqm bldg area" : ""} </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="area_basis">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Area Basis
              </Form.Label>
              <Col>
                <Select value={state.selectedAreaBasisCategories} onChange={onAreaBasisChange} options={derivedAreaBasisOptions} inputId={createId("field", "area-basis", "input")} instanceId={createId("field", "area-basis", "instance")} isClearable />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="site_area">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Site Area
              </Form.Label>
              <Col>
                <Form.Control onChange={onNumberChange} value={numberFormatter.format(state.site_area)} as="input" type="text" onBlur={onBlur} />
              </Col>
              <Col>{state.sale_price && state.site_area ? currencyFormatter.format((parseFloat(state.sale_price.replace(/[^0-9.]/g, "")) || 0) / (state.site_area || 1)) + " per sqm site area" : ""} </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="cars">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Car Bays
              </Form.Label>
              <Col>
                <Form.Control onChange={onNumberChange} value={state.cars} as="input" type="text" onBlur={onBlur} />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="car_space_describer">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Car Space Describer
              </Form.Label>
              <Col>
                <Select value={state.selectedCarSpaceCategories} onChange={onCarSpaceChange} options={derivedCarSpaceOptions} inputId={createId("field", "car-space-describer", "input")} instanceId={createId("field", "car-space-describer", "instance")} isClearable />
              </Col>
            </Form.Group>

            <Form.Group as={Row} controlId="frontage">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Frontage
              </Form.Label>
              <Col>
                <Form.Control onChange={onNumberChange} value={state.frontage} as="input" type="text" onBlur={onBlur} />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="quality_type">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Quality Type
              </Form.Label>
              <Col>
                <Select value={state.selectedQualityTypeCategories} onChange={onQualityTypeChange} options={derivedQualityTypeOptions} inputId={createId("field", "quality-type", "input")} instanceId={createId("field", "quality-type", "instance")} isClearable />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="zoning">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Zoning
              </Form.Label>
              <Col>
                <Select value={state.selectedZoningCategories} onChange={onZoningChange} options={derivedZoningOptions} inputId={createId("field", "zoning", "input")} instanceId={createId("field", "zoning", "instance")} isClearable />
              </Col>
            </Form.Group>

            <Form.Group as={Row} controlId="floor_space_ratio">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Floor Space Ratio
              </Form.Label>
              <Col>
                <Form.Control onChange={onChange} value={state.floor_space_ratio} as="input" type="text" onBlur={onBlur} />
              </Col>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group as={Row} controlId="sales_notes">
          <Form.Label>
            <b>Extra sales notes (appear to customers)</b>
          </Form.Label>
          <Form.Control onChange={onChange} onBlur={onBlur} value={state.sales_notes} as="textarea" type="text" rows="2" />
        </Form.Group>

        <Form.Group as={Row} controlId="sales_notes_computed">
          <Form.Label
            style={{
              width: "100%",
              marginTop: "5px",
            }}
          >
            <div className="slm-sale-new-notes-header-row">
              <div className="slm-sale-new-notes-title-wrap">
                <b>New auto-generated Sales Notes</b>
              </div>
              <div className="slm-sale-new-notes-right-wrap">
                {goLiveDate <= new Date(state.print_month) && (
                  <b className="slm-sale-new-notes-status-text">
                    **Currently visible to customers**
                  </b>
                )}
                <OverlayTrigger
                  trigger="click"
                  key="info"
                  placement="top"
                  overlay={
                    <Popover
                      id={createId("popover", "sales-notes-info")}
                      style={{
                        maxWidth: "800px",
                      }}
                    >
                      <Popover.Header as="h3">Info</Popover.Header>
                      <Popover.Body>
                        {`In April 2023 - as part of the changes required for Commercial APIs - we moved the entry of lots/plans, volumes/folios and Transfer Number from the free-text Sale Notes, into separate Parcels and Titles References tables. 
We also introduced a "New auto-generated Sales Notes" field - which automatically creates Sales Notes text for PIM customers. 
We also retained the "Old Sales Notes - pre API 2023 changes".
Which does the customer see?
For older records with a Delivery Date earlier than the changes introduced April 2023, by default the customer sees the "Old Sales Notes - pre API 2023 changes". 
For new records since April 2023, the customer will see the "New auto-generated Sales Notes".
This also applies for older records redelivered since April 2023.
To make it clear which the customer is seeing, the text "**Currently visible to customers**" appears.
Note: When a new record is entered or an older record is redelivered, the onus is on the user to check the "New auto-generated Sales Notes" field appears correct.
The "New auto-generated Sales Notes" field cannot be edited directly and is built from Parcels, Volume/Folios, Transfer Number (where SOLD) and any entered "Extra sales notes".`}
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button
                    id={createId("action", "sales-notes-info", "button")}
                    className="d-inline-flex align-items-center justify-content-center slm-sale-new-notes-info-btn"
                    variant="link"
                    size="sm"
                    aria-label="Sales notes info"
                  >
                    <Info />
                  </Button>
                </OverlayTrigger>
              </div>
            </div>
          </Form.Label>
          <Form.Control onChange={onChange} onBlur={onBlur} value={typeof state.sales_notes_computed === "string" ? state.sales_notes_computed.trim() : state.sales_notes_computed} as="textarea" type="text" rows="5" disabled />
        </Form.Group>

        <Form.Group as={Row} controlId="note_data">
          <Form.Label
            style={{
              width: "100%",
              paddingLeft: 0,
              paddingRight: 0,
              marginBottom: "4px",
            }}
          >
            <div
              className="slm-sale-old-notes-header-row"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                flexWrap: "wrap",
                columnGap: "4px",
                rowGap: "2px",
              }}
            >
              <div
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  textAlign: "left",
                }}
              >
                <b>Old Sales Notes - pre API 2023 changes</b>
              </div>
              {goLiveDate > new Date(state.print_month) && (
                <div
                  className="slm-sale-old-notes-status-wrap"
                  style={{
                    flex: "0 1 auto",
                    minWidth: 0,
                    marginLeft: "auto",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <b
                    className="slm-sale-old-notes-status-text"
                    style={{
                      color: "red",
                      textAlign: "right",
                    }}
                  >
                    **Currently visible to customers**
                  </b>
                </div>
              )}
            </div>
          </Form.Label>
          <Form.Control onChange={onChange} onBlur={onBlur} value={state.note_data} as="textarea" type="text" rows="5" disabled />
        </Form.Group>
      </Card.Body>
    </Card>
  );
}
